/**
 * invitesRouter — RPC contract for the invites router, the domain's one deployed
 * callable. Six acts ride it, each an `action` on the payload. Three are the
 * issuer's: `createInvite` mints a pending invitation, `editInvite` patches
 * its role or the invitee's name in place, `resendInvite` supersedes it with
 * a fresh token, `revokeInvite` withdraws it. Two are the addressee's:
 * `acceptInvitation` joins the organization, `declineInvitation` turns the
 * offer down. Parsed here by `firebase/src/invites/invites.router.ts` and
 * composed by every client that calls it.
 *
 * EVERY ARM COMPOSES FROM ITS OWN CONTRACT FILE. Each act is fired from a
 * different screen by a different principal and already carries its own
 * `Input`/`Output` beside its own justification, so nothing is redeclared
 * here: each input spreads onto this level through `.extend()`, which keeps
 * the source's `.strict()` — an extra key still fails the whole request.
 * All six inputs are flat objects, so each is a legal option of the
 * discriminated union; `CreateInviteInput.scope` is itself a discriminated
 * union, but it sits under its own key, a nested value rather than a
 * top-level discrimination.
 *
 * THE ACTION NAMES ARE THE NAMES `.requiresHandler` DECLARES. `invite.entity.ts`
 * marks `create` `.requiresHandler("createInvite")`, `revoke`
 * `.requiresHandler("revokeInvite")`, `edit` `.requiresHandler("editInvite")`,
 * `accept` `.requiresHandler("acceptInvitation")` and `decline`
 * `.requiresHandler("declineInvitation")`: the marker names the act that
 * performs the verb, never the function it deploys inside, so every arm keeps
 * the marker's own spelling and the markers stay true. `resendInvite` carries
 * no marker (a resend is a create and a revoke, both already marked) and keeps
 * its own callable's name, so the six read as one set.
 *
 * `action` IS ADDRESSING, NOT DATA. The router takes the discriminant back off
 * before the payload crosses into a handler: a handler that received it would
 * carry a field its own contract does not declare, and `createInvite` feeds
 * its payload onward into `InviteSchema.parse`, which refuses exactly that.
 *
 * THE ENVELOPE ASSERTS NO PERMISSION, because the six share none. The two
 * answers are redemption paths whose caller holds no grant anywhere — their
 * authority is being the addressee, read off the session's `email_verified`
 * claim. The four issuing acts want `tickets.members.invite` at the
 * organization the payload names, with the second factor, and a containment
 * check against a document only the parsed payload resolves. The intersection
 * is authentication, which is the whole of what the router's gate asserts;
 * every further check stays inside its handler.
 *
 * Fields justified:
 *   action — selects the handler, and which payload shape applies.
 *   (every other field) — the matching act's own fields; each source
 *            contract carries its own justification.
 */
import { z } from "zod";
import {
	AcceptInvitationInput,
	AcceptInvitationOutput,
} from "./acceptInvitation.action.ts";
import {
	CreateInviteInput,
	CreateInviteOutput,
} from "./createInvite.action.ts";
import {
	DeclineInvitationInput,
	DeclineInvitationOutput,
} from "./declineInvitation.action.ts";
import { EditInviteInput, EditInviteOutput } from "./editInvite.action.ts";
import {
	ResendInviteInput,
	ResendInviteOutput,
} from "./resendInvite.action.ts";
import {
	RevokeInviteInput,
	RevokeInviteOutput,
} from "./revokeInvite.action.ts";

export const InvitesInput = z.discriminatedUnion("action", [
	CreateInviteInput.extend({ action: z.literal("createInvite") }),
	EditInviteInput.extend({ action: z.literal("editInvite") }),
	ResendInviteInput.extend({ action: z.literal("resendInvite") }),
	RevokeInviteInput.extend({ action: z.literal("revokeInvite") }),
	AcceptInvitationInput.extend({ action: z.literal("acceptInvitation") }),
	DeclineInvitationInput.extend({ action: z.literal("declineInvitation") }),
]);
export type InvitesInput = z.infer<typeof InvitesInput>;

// The six answers share no field to discriminate on — two minted tokens and
// four bare acknowledgements — so this stays a plain union. All six are listed
// rather than deduplicated, so the union follows its own contract the day one
// of them answers with more.
export const InvitesOutput = z.union([
	CreateInviteOutput,
	EditInviteOutput,
	ResendInviteOutput,
	RevokeInviteOutput,
	AcceptInvitationOutput,
	DeclineInvitationOutput,
]);
export type InvitesOutput = z.infer<typeof InvitesOutput>;
